import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Stack,
  Pagination,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Grid,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Avatar,
  Autocomplete,
  InputAdornment,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { toast } from 'react-toastify';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import {
  fetchCurrentDonationCard,
  createDonationCard,
  updateDonationCard,
  uploadDonationCardImage,
  fetchDonationCardLogoDefaults,
  fetchAllDonationCardPurchases,
  fetchDonationCardPurchaseById,
  approveDonationCardPayment,
  cancelDonationCardPurchase,
  createDonationCardPurchase,
  searchDonationCardPurchases,
  fetchDonationStatistics,
  fetchTopDonors,
  fetchTopRecipients,
} from '../../controllers/paymentsController';
import { searchMembers, searchMembersWithSubMembers } from '../../controllers/memberManagementController';
import { resolveGroupsAssetUrl } from '../../api/httpClientGroups';
import { resolvePaymentsFileUrl } from '../../api/httpClientPayments';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import FilterListIcon from '@mui/icons-material/FilterList';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PaymentsIcon from '@mui/icons-material/Payments';
import ScheduleIcon from '@mui/icons-material/Schedule';
import DonationLogoInfoSection from './components/DonationLogoInfoSection';
import {
  FALLBACK_DONATION_CARD_LOGO_INCOME_ARP_CODE,
  buildDonationCardLogoIncomeHelperText,
  normalizeLogoIncomeArpCode,
  resolveEffectiveLogoIncomeArpCode,
} from './productFormUtils';

import {
  getCurrencyDisplayLabel,
} from '../../utils/currencyDisplay';

// Enum değerleri
const DonationCardPurchaseStatus = {
  Pending: 0,
  Paid: 1,
  Cancelled: 2,
  AwaitingApproval: 3,
};

const DonationCardPurchaseStatusNames = {
  0: 'Beklemede',
  1: 'Ödendi',
  2: 'İptal Edildi',
  3: 'Onay Bekliyor',
};

const PaymentMethodNames = {
  0: 'Nakit',
  1: 'Banka Havalesi/EFT',
  2: 'Kredi Kartı',
  3: 'Online Ödeme',
};

const DonationCards = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState(0);

  // Tab 1: Bağış Kartı Ayarları
  const [card, setCard] = useState(null);
  const [cardLoading, setCardLoading] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    id: '',
    description: '',
    amount: 0,
    currency: 'TRY',
    isActive: true,
    logoIncomeArpCode: '',
  });
  const [saving, setSaving] = useState(false);
  const [logoIncomeDefaults, setLogoIncomeDefaults] = useState({
    defaultLogoIncomeArpCode: FALLBACK_DONATION_CARD_LOGO_INCOME_ARP_CODE,
  });

  // Kart oluşturma (Create) dialog state'leri
  const [createCardDialogOpen, setCreateCardDialogOpen] = useState(false);
  const [createCardForm, setCreateCardForm] = useState({
    description: '',
    amount: 0,
    currency: 'TRY',
    isActive: true,
    logoIncomeArpCode: '',
    imageFile: null,
  });
  const [creatingCard, setCreatingCard] = useState(false);

  // Kart görseli yükleme state'i
  const [imageUploading, setImageUploading] = useState(false);

  // Tab 2: Satın Alımlar
  const [purchases, setPurchases] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Filtreler
  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState(0); // 0=CreatedAtDesc (default)
  const [purchasedByPerson, setPurchasedByPerson] = useState(null);
  const [donatedToPerson, setDonatedToPerson] = useState(null);
  const [purchasedByOptions, setPurchasedByOptions] = useState([]);
  const [donatedToOptions, setDonatedToOptions] = useState([]);
  const [purchasedByLoading, setPurchasedByLoading] = useState(false);
  const [donatedToLoading, setDonatedToLoading] = useState(false);
  const [filterAccordionOpen, setFilterAccordionOpen] = useState(false);

  // Dialog state'leri
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [approveDialogOpen, setApproveDialogOpen] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [purchaseDetail, setPurchaseDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [approveNotes, setApproveNotes] = useState('');
  const [approvePaymentDate, setApprovePaymentDate] = useState('');
  const [cancelReason, setCancelReason] = useState('');
  const [processing, setProcessing] = useState(false);

  // İstatistikler
  const [statistics, setStatistics] = useState(null);
  const [statisticsLoading, setStatisticsLoading] = useState(false);
  const [topDonors, setTopDonors] = useState([]);
  const [topRecipients, setTopRecipients] = useState([]);
  const [topDonorsLoading, setTopDonorsLoading] = useState(false);
  const [topRecipientsLoading, setTopRecipientsLoading] = useState(false);

  // Create Dialog state'leri
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [createForm, setCreateForm] = useState({
    purchasedByPerson: null,
    purchasedByType: 0, // 0=Member, 1=SubMember
    donatedToPerson: null,
    donatedToType: 0, // 0=Member, 1=SubMember
    quantity: 1,
    paymentMethod: 0, // 0=Cash, 1=BankTransfer, 2=CreditCard, 3=Online
    referenceNumber: '',
    description: '',
    paymentProofUrl: '',
    paymentDate: '',
  });
  const [purchasedBySearchOptions, setPurchasedBySearchOptions] = useState([]);
  const [donatedToSearchOptions, setDonatedToSearchOptions] = useState([]);
  const [purchasedBySearchLoading, setPurchasedBySearchLoading] = useState(false);
  const [donatedToSearchLoading, setDonatedToSearchLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  const defaultLogoIncomeArpCode = logoIncomeDefaults.defaultLogoIncomeArpCode
    || FALLBACK_DONATION_CARD_LOGO_INCOME_ARP_CODE;
  const logoIncomeHelperText = buildDonationCardLogoIncomeHelperText(defaultLogoIncomeArpCode);

  const loadLogoDefaults = useCallback(async () => {
    try {
      const defaults = await fetchDonationCardLogoDefaults();
      if (defaults?.defaultLogoIncomeArpCode) {
        setLogoIncomeDefaults(defaults);
      }
    } catch {
      // fallback kullan
    }
  }, []);

  // Bağış kartını yükle
  const loadCard = async () => {
    setCardLoading(true);
    try {
      const data = await fetchCurrentDonationCard();
      setCard(data);
    } catch (e) {
      toast.error(e?.message || 'Bağış kartı yüklenemedi');
    } finally {
      setCardLoading(false);
    }
  };

  // Satın alımları yükle (Search endpoint kullanarak)
  const loadPurchases = async () => {
    setLoading(true);
    setError('');
    try {
      const payload = {
        pageNumber: page,
        pageSize,
        sortBy: sortBy,
      };
      if (statusFilter !== '') payload.status = Number(statusFilter);
      if (searchTerm?.trim()) payload.searchTerm = searchTerm.trim();
      if (startDate) {
        // Date input'tan gelen YYYY-MM-DD formatını ISO string'e çevir
        payload.startDate = new Date(startDate + 'T00:00:00').toISOString();
      }
      if (endDate) {
        // Date input'tan gelen YYYY-MM-DD formatını ISO string'e çevir (günün sonuna ayarla)
        payload.endDate = new Date(endDate + 'T23:59:59').toISOString();
      }
      if (purchasedByPerson?.id) payload.purchasedByPersonId = purchasedByPerson.id;
      if (donatedToPerson?.id) payload.donatedToPersonId = donatedToPerson.id;

      const { data, meta } = await searchDonationCardPurchases(payload);
      setPurchases(data || []);
      setTotalPages(meta?.totalPages || 1);
    } catch (e) {
      setError(e?.message || 'Satın alımlar yüklenemedi');
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  // İstatistikleri yükle
  const loadStatistics = async () => {
    setStatisticsLoading(true);
    try {
      const data = await fetchDonationStatistics({});
      setStatistics(data);
    } catch (e) {
      // Hata durumunda sessizce devam et
    } finally {
      setStatisticsLoading(false);
    }
  };

  // Top Donors yükle
  const loadTopDonors = async () => {
    setTopDonorsLoading(true);
    try {
      const data = await fetchTopDonors({ topCount: 10 });
      setTopDonors(data || []);
    } catch (e) {
      // Hata durumunda sessizce devam et
      setTopDonors([]);
    } finally {
      setTopDonorsLoading(false);
    }
  };

  // Top Recipients yükle
  const loadTopRecipients = async () => {
    setTopRecipientsLoading(true);
    try {
      const data = await fetchTopRecipients({ topCount: 10 });
      setTopRecipients(data || []);
    } catch (e) {
      // Hata durumunda sessizce devam et
      setTopRecipients([]);
    } finally {
      setTopRecipientsLoading(false);
    }
  };

  useEffect(() => {
    loadLogoDefaults();
  }, [loadLogoDefaults]);

  useEffect(() => {
    if (tab === 0) {
      loadPurchases();
      loadStatistics();
      loadTopDonors();
      loadTopRecipients();
    } else if (tab === 1) {
      loadCard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, page, statusFilter, startDate, endDate, sortBy, purchasedByPerson?.id, donatedToPerson?.id]);

  // Search term, startDate, endDate değiştiğinde yükle (debounce)
  useEffect(() => {
    if (tab === 0) {
      const timer = setTimeout(() => {
        loadPurchases();
      }, 500);
      return () => clearTimeout(timer);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, startDate, endDate, tab]);

  // Kart düzenleme dialog'unu aç
  const handleOpenEditDialog = () => {
    if (card) {
      setEditForm({
        id: card.id,
        description: card.description || '',
        amount: card.amount || 0,
        currency: card.currency || 'TRY',
        isActive: card.isActive ?? true,
        logoIncomeArpCode: card.logoIncomeArpCode ?? card.defaultLogoIncomeArpCode ?? defaultLogoIncomeArpCode,
      });
      setEditDialogOpen(true);
    }
  };

  // Kart oluşturma dialog'unu aç
  const handleOpenCreateCardDialog = () => {
    setCreateCardForm({
      description: '',
      amount: 0,
      currency: 'TRY',
      isActive: true,
      logoIncomeArpCode: defaultLogoIncomeArpCode,
      imageFile: null,
    });
    setCreateCardDialogOpen(true);
  };

  // Kartı güncelle
  const handleUpdateCard = async () => {
    setSaving(true);
    try {
      const updated = await updateDonationCard({
        ...editForm,
        logoIncomeArpCode: normalizeLogoIncomeArpCode(editForm.logoIncomeArpCode),
      });
      setCard(updated);
      setEditDialogOpen(false);
      toast.success('Bağış kartı güncellendi');
    } catch (e) {
      toast.error(e?.message || 'Bağış kartı güncellenemedi');
    } finally {
      setSaving(false);
    }
  };

  // Yeni bağış kartı oluştur (görsel dahil)
  const handleCreateCard = async () => {
    if (!createCardForm.amount || createCardForm.amount <= 0) {
      toast.error('Geçerli bir tutar giriniz.');
      return;
    }
    setCreatingCard(true);
    try {
      const created = await createDonationCard({
        ...createCardForm,
        logoIncomeArpCode: normalizeLogoIncomeArpCode(createCardForm.logoIncomeArpCode),
      });
      setCard(created);
      setCreateCardDialogOpen(false);
      toast.success('Bağış kartı oluşturuldu');
    } catch (e) {
      toast.error(e?.message || 'Bağış kartı oluşturulamadı');
    } finally {
      setCreatingCard(false);
    }
  };

  // Kart görselini güncelle
  const handleCardImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !card) return;

    setImageUploading(true);
    try {
      await uploadDonationCardImage({ cardId: card.id, file });
      toast.success('Bağış kartı görseli güncellendi');
      await loadCard();
    } catch (err) {
      toast.error(err?.message || 'Bağış kartı görseli güncellenemedi');
    } finally {
      setImageUploading(false);
      // Aynı dosyanın tekrar seçilebilmesi için input'u sıfırla
      e.target.value = '';
    }
  };

  // Satın alım detayını yükle
  const handleViewDetail = useCallback(async (purchase) => {
    if (!purchase?.id) return;
    setSelectedPurchase(purchase);
    setDetailLoading(true);
    setDetailDialogOpen(true);
    setTab(0);
    try {
      const detail = await fetchDonationCardPurchaseById(purchase.id);
      setPurchaseDetail(detail);
      setSelectedPurchase(detail);
    } catch (e) {
      toast.error(e?.message || 'Detay yüklenemedi');
      setPurchaseDetail(null);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    const purchaseId = searchParams.get('purchaseId');
    if (!purchaseId) return;
    handleViewDetail({ id: purchaseId });
    const next = new URLSearchParams(searchParams);
    next.delete('purchaseId');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, handleViewDetail]);

  // Ödeme onayla
  const handleApprove = async () => {
    const target = purchaseDetail || selectedPurchase;
    if (!target) return;
    setProcessing(true);
    try {
      await approveDonationCardPayment({
        purchaseId: target.id,
        paymentDate: approvePaymentDate || new Date().toISOString(),
        notes: approveNotes || null,
      });
      toast.success('Ödeme onaylandı');
      setApproveDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPurchase(null);
      setApproveNotes('');
      setApprovePaymentDate('');
      loadPurchases();
      loadStatistics();
      loadTopDonors();
      loadTopRecipients();
    } catch (e) {
      toast.error(e?.message || 'Ödeme onaylanamadı');
    } finally {
      setProcessing(false);
    }
  };

  // Satın alımı iptal et
  const handleCancel = async () => {
    const target = purchaseDetail || selectedPurchase;
    if (!target) return;
    setProcessing(true);
    try {
      await cancelDonationCardPurchase({
        purchaseId: target.id,
        cancellationReason: cancelReason || null,
      });
      toast.success('Satın alım iptal edildi');
      setCancelDialogOpen(false);
      setDetailDialogOpen(false);
      setSelectedPurchase(null);
      setCancelReason('');
      loadPurchases();
      loadStatistics();
      loadTopDonors();
      loadTopRecipients();
    } catch (e) {
      toast.error(e?.message || 'Satın alım iptal edilemedi');
    } finally {
      setProcessing(false);
    }
  };

  // Durum rengi
  const getStatusColor = (status) => {
    switch (status) {
      case DonationCardPurchaseStatus.Paid:
        return 'success';
      case DonationCardPurchaseStatus.AwaitingApproval:
        return 'warning';
      case DonationCardPurchaseStatus.Cancelled:
        return 'error';
      default:
        return 'info';
    }
  };

  const activePurchase = purchaseDetail || selectedPurchase;

  // Onaylanabilir mi?
  const canApprove = useMemo(
    () => activePurchase && activePurchase.status === DonationCardPurchaseStatus.AwaitingApproval,
    [activePurchase],
  );

  // İptal edilebilir mi?
  const canCancel = useMemo(
    () => activePurchase
      && activePurchase.status !== DonationCardPurchaseStatus.Paid
      && activePurchase.status !== DonationCardPurchaseStatus.Cancelled,
    [activePurchase],
  );

  // Member search handlers
  const handlePurchasedBySearch = async (query) => {
    if (!query || query.length < 2) {
      setPurchasedByOptions([]);
      return;
    }
    setPurchasedByLoading(true);
    try {
      const results = await searchMembers(query);
      setPurchasedByOptions(results || []);
    } catch (e) {
      setPurchasedByOptions([]);
    } finally {
      setPurchasedByLoading(false);
    }
  };

  const handleDonatedToSearch = async (query) => {
    if (!query || query.length < 2) {
      setDonatedToOptions([]);
      return;
    }
    setDonatedToLoading(true);
    try {
      const results = await searchMembers(query);
      setDonatedToOptions(results || []);
    } catch (e) {
      setDonatedToOptions([]);
    } finally {
      setDonatedToLoading(false);
    }
  };

  // Filtreleri sıfırla
  const handleResetFilters = () => {
    setStatusFilter('');
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSortBy(0);
    setPurchasedByPerson(null);
    setDonatedToPerson(null);
    setPage(1);
  };

  // Create Dialog handlers
  const handleOpenCreateDialog = () => {
    setCreateForm({
      purchasedByPerson: null,
      purchasedByType: 0,
      donatedToPerson: null,
      donatedToType: 0,
      quantity: 1,
      paymentMethod: 0,
      referenceNumber: '',
      description: '',
      paymentProofUrl: '',
      paymentDate: '',
    });
    setPurchasedBySearchOptions([]);
    setDonatedToSearchOptions([]);
    setCreateDialogOpen(true);
  };

  const handlePurchasedBySearchForCreate = async (query) => {
    if (!query || query.length < 2) {
      setPurchasedBySearchOptions([]);
      return;
    }
    setPurchasedBySearchLoading(true);
    try {
      const data = await searchMembersWithSubMembers(query);
      const flattened = [];
      (Array.isArray(data) ? data : []).forEach((item) => {
        if (!item) return;
        if (typeof item.type === 'number') {
          flattened.push({
            id: item.id,
            fullName: item.fullName,
            profilePictureUrl: item.profilePictureUrl,
            type: item.type, // 0=Member, 1=SubMember
            parentMemberId: item.parentMemberId || null,
          });
        } else {
          flattened.push({
            id: item.id,
            fullName: item.fullName,
            profilePictureUrl: item.profilePictureUrl,
            type: 0, // Member
          });
          if (Array.isArray(item.subMembers)) {
            item.subMembers.forEach((s) => {
              if (!s) return;
              flattened.push({
                id: s.id,
                fullName: s.fullName,
                profilePictureUrl: s.profilePictureUrl,
                type: 1, // SubMember
                parentMemberId: item.id,
              });
            });
          }
        }
      });
      setPurchasedBySearchOptions(flattened);
    } catch (e) {
      setPurchasedBySearchOptions([]);
    } finally {
      setPurchasedBySearchLoading(false);
    }
  };

  const handleDonatedToSearchForCreate = async (query) => {
    if (!query || query.length < 2) {
      setDonatedToSearchOptions([]);
      return;
    }
    setDonatedToSearchLoading(true);
    try {
      const data = await searchMembersWithSubMembers(query);
      const flattened = [];
      (Array.isArray(data) ? data : []).forEach((item) => {
        if (!item) return;
        if (typeof item.type === 'number') {
          flattened.push({
            id: item.id,
            fullName: item.fullName,
            profilePictureUrl: item.profilePictureUrl,
            type: item.type, // 0=Member, 1=SubMember
            parentMemberId: item.parentMemberId || null,
          });
        } else {
          flattened.push({
            id: item.id,
            fullName: item.fullName,
            profilePictureUrl: item.profilePictureUrl,
            type: 0, // Member
          });
          if (Array.isArray(item.subMembers)) {
            item.subMembers.forEach((s) => {
              if (!s) return;
              flattened.push({
                id: s.id,
                fullName: s.fullName,
                profilePictureUrl: s.profilePictureUrl,
                type: 1, // SubMember
                parentMemberId: item.id,
              });
            });
          }
        }
      });
      setDonatedToSearchOptions(flattened);
    } catch (e) {
      setDonatedToSearchOptions([]);
    } finally {
      setDonatedToSearchLoading(false);
    }
  };

  const handleCreatePurchase = async () => {
    if (!createForm.purchasedByPerson || !createForm.donatedToPerson || !createForm.quantity || createForm.quantity < 1 || createForm.quantity > 999) {
      toast.error('Lütfen tüm gerekli alanları doldurun');
      return;
    }
    setCreating(true);
    try {
      const payload = {
        purchasedByPersonId: createForm.purchasedByPerson.id,
        purchasedByType: createForm.purchasedByType,
        donatedToPersonId: createForm.donatedToPerson.id,
        donatedToType: createForm.donatedToType,
        quantity: createForm.quantity,
        paymentMethod: createForm.paymentMethod,
      };
      if (createForm.referenceNumber?.trim()) {
        payload.referenceNumber = createForm.referenceNumber.trim();
      }
      if (createForm.description?.trim()) {
        payload.description = createForm.description.trim();
      }
      if (createForm.paymentProofUrl?.trim()) {
        payload.paymentProofUrl = createForm.paymentProofUrl.trim();
      }
      if (createForm.paymentDate) {
        payload.paymentDate = new Date(createForm.paymentDate).toISOString();
      }
      await createDonationCardPurchase(payload);
      toast.success('Bağış kartı satın alımı oluşturuldu');
      setCreateDialogOpen(false);
      setCreateForm({
        purchasedByPerson: null,
        purchasedByType: 0,
        donatedToPerson: null,
        donatedToType: 0,
        quantity: 1,
        paymentMethod: 0,
        referenceNumber: '',
        description: '',
        paymentProofUrl: '',
        paymentDate: '',
      });
      loadPurchases();
      loadStatistics();
      loadTopDonors();
      loadTopRecipients();
    } catch (e) {
      toast.error(e?.message || 'Bağış kartı satın alımı oluşturulamadı');
    } finally {
      setCreating(false);
    }
  };

  const textFieldSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: '5px',
      '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#0E4D94' },
    },
    '& .MuiInputBase-input': { py: 1.2, px: 1.5, fontSize: '14px' },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e6e6e6' },
  };

  const tableCellHeadSx = {
    fontSize: '14px',
    fontWeight: 500,
    borderBottom: '1px dashed #e8e8e8',
    px: 1.5,
    py: 1.5,
    color: '#a3a3a3',
  };

  const hasActiveFilters = !!(searchTerm || startDate || endDate || statusFilter || purchasedByPerson || donatedToPerson || sortBy !== 0);

  return (
    <Box sx={{ width: '100%' }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 2 }}>
        <Typography sx={{ fontSize: 18, fontWeight: 500 }}>Bağış Kartları</Typography>
      </Stack>

      <Tabs
        value={tab}
        onChange={(e, v) => {
          setTab(v);
          setPage(1);
          setStatusFilter('');
          setSearchTerm('');
          setStartDate('');
          setEndDate('');
          setSortBy(0);
          setPurchasedByPerson(null);
          setDonatedToPerson(null);
        }}
        sx={{
          mb: 2,
          '& .MuiTab-root': {
            textTransform: 'none',
            fontSize: '14px',
            fontWeight: 500,
            color: '#a3a3a3',
            '&.Mui-selected': { color: '#0E4D94' },
          },
          '& .MuiTabs-indicator': {
            backgroundColor: '#0E4D94',
          },
        }}
      >
        <Tab label="Satın Alımlar" />
        <Tab label="Bağış Kartı Ayarları" />
      </Tabs>

      {/* Tab 1: Satın Alımlar */}
      {tab === 0 && (
        <>
          <Alert severity="info" sx={{ mb: 2, fontSize: '13px' }}>
            PayTR ile yapılan kart ödemelerinde Logo borç (04) ve tahsilat (08) fişleri otomatik oluşur.
            Admin nakit/havale kayıtları Logo üretmez.
          </Alert>
          {/* Bağış Özeti - Dashboard kartları ile uyumlu tasarım */}
          <Grid container spacing={2} sx={{ mb: 3, width: '100%' }}>
            {/* İlk 4 kart - 6'lı grid, sabit genişlik, yan yana */}
            {statistics && (
              <>
                <Grid item xs={12} sx={{ width: '100%', minWidth: 0 }}>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(4, 1fr)', md: 'repeat(6, 1fr)' },
                      gap: 2,
                      width: '100%',
                    }}
                  >
                    <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', minWidth: 0 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                          <PaymentsIcon sx={{ fontSize: 20, color: '#0E4D94' }} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666666', mb: 0.5 }}>Toplam Bağış</Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#444444' }}>{statistics.totalDonationCount || 0}</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', minWidth: 0 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: '#17C653' }} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666666', mb: 0.5 }}>Ödenen Bağış</Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#444444' }}>{statistics.paidDonationCount || 0}</Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', minWidth: 0 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f5f5f5', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                          <PaymentsIcon sx={{ fontSize: 20, color: '#0E4D94' }} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666666', mb: 0.5 }}>Toplam Tutar</Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#444444', lineHeight: 1.2 }}>
                          {statistics.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}{' '}
                          <Box component="span" sx={{ fontSize: '12px', fontWeight: 500, color: '#a3a3a3' }}>{getCurrencyDisplayLabel(statistics.currency)}</Box>
                        </Typography>
                      </CardContent>
                    </Card>
                    <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', minWidth: 0 }}>
                      <CardContent sx={{ p: 2.5 }}>
                        <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                          <CheckCircleIcon sx={{ fontSize: 20, color: '#17C653' }} />
                        </Box>
                        <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666666', mb: 0.5 }}>Ödenen Tutar</Typography>
                        <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#444444', lineHeight: 1.2 }}>
                          {statistics.paidAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}{' '}
                          <Box component="span" sx={{ fontSize: '12px', fontWeight: 500, color: '#a3a3a3' }}>{getCurrencyDisplayLabel(statistics.currency)}</Box>
                        </Typography>
                      </CardContent>
                    </Card>
                    {statistics.pendingDonationCount !== undefined && (
                      <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', minWidth: 0 }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                            <ScheduleIcon sx={{ fontSize: 20, color: '#ea580c' }} />
                          </Box>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666666', mb: 0.5 }}>Bekleyen</Typography>
                          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#444444' }}>{statistics.pendingDonationCount || 0}</Typography>
                        </CardContent>
                      </Card>
                    )}
                    {statistics.awaitingApprovalCount !== undefined && (
                      <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', minWidth: 0 }}>
                        <CardContent sx={{ p: 2.5 }}>
                          <Box sx={{ width: 40, height: 40, borderRadius: '50%', backgroundColor: '#E7EDF4', display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                            <ScheduleIcon sx={{ fontSize: 20, color: '#0E4D94' }} />
                          </Box>
                          <Typography sx={{ fontSize: '13px', fontWeight: 500, color: '#666666', mb: 0.5 }}>Onay Bekleyen</Typography>
                          <Typography sx={{ fontSize: '22px', fontWeight: 700, color: '#444444' }}>{statistics.awaitingApprovalCount || 0}</Typography>
                        </CardContent>
                      </Card>
                    )}
                  </Box>
                </Grid>
              </>
            )}

            {/* Top Donors & Top Recipients - Tablo benzeri liste */}
          </Grid>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 2,
              width: '100%',
              mb: 3,
            }}
          >
            <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', height: '100%', minWidth: 0 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 2 }}>En Çok Bağış Yapanlar</Typography>
              {topDonorsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: '#0E4D94' }} />
                </Box>
              ) : topDonors.length === 0 ? (
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>Veri bulunamadı</Typography>
              ) : (
                <Stack spacing={0}>
                  {topDonors.slice(0, 5).map((donor, index) => (
                    <Stack
                      key={donor.personId || index}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        py: 1.25,
                        borderBottom: index < 4 ? '1px dashed #e8e8e8' : 'none',
                      }}
                    >
                      <Avatar src={resolveGroupsAssetUrl(donor.profilePictureUrl)} sx={{ width: 32, height: 32, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '14px', color: '#444444', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {donor.fullName || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', flexShrink: 0 }}>
                        {donor.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} {getCurrencyDisplayLabel(donor.currency)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Card>
            <Card sx={{ p: 2.5, border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px', height: '100%', minWidth: 0 }}>
              <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', mb: 2 }}>En Çok Bağış Alanlar</Typography>
              {topRecipientsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                  <CircularProgress size={24} sx={{ color: '#0E4D94' }} />
                </Box>
              ) : topRecipients.length === 0 ? (
                <Typography sx={{ fontSize: '13px', color: '#a3a3a3' }}>Veri bulunamadı</Typography>
              ) : (
                <Stack spacing={0}>
                  {topRecipients.slice(0, 5).map((recipient, index) => (
                    <Stack
                      key={recipient.personId || index}
                      direction="row"
                      spacing={1.5}
                      alignItems="center"
                      sx={{
                        py: 1.25,
                        borderBottom: index < 4 ? '1px dashed #e8e8e8' : 'none',
                      }}
                    >
                      <Avatar src={resolveGroupsAssetUrl(recipient.profilePictureUrl)} sx={{ width: 32, height: 32, flexShrink: 0 }} />
                      <Typography sx={{ fontSize: '14px', color: '#444444', flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {recipient.fullName || '-'}
                      </Typography>
                      <Typography sx={{ fontSize: '14px', fontWeight: 500, color: '#444444', flexShrink: 0 }}>
                        {recipient.totalAmount?.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'} {getCurrencyDisplayLabel(recipient.currency)}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              )}
            </Card>
          </Box>

          {error && (
            <Alert severity="error" sx={{ mb: 2, fontSize: '13px' }}>
              {error}
            </Alert>
          )}

          {/* Tablo kartı */}
          <Box
            sx={{
              border: '1px solid #f1f1f1',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
              borderRadius: '8px',
              overflow: 'hidden',
            }}
          >
            {/* Tablo kartı içi - Yeni Bağış Ekle ve Filtrele butonları */}
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ px: 2.5, py: 1.5, backgroundColor: '#ffffff' }}>
              <Button
                variant="contained"
                size="small"
                startIcon={<AddIcon sx={{ width: 16 }} />}
                onClick={handleOpenCreateDialog}
                sx={{
                  textTransform: 'none',
                  backgroundColor: '#E7EDF4',
                  boxShadow: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  color: '#0E4D94',
                  '&:hover': { backgroundColor: '#CFDBEA', boxShadow: 'none' },
                }}
              >
                Yeni Bağış Ekle
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<FilterListIcon sx={{ fontSize: 18 }} />}
                onClick={() => setFilterAccordionOpen((o) => !o)}
                sx={{
                  textTransform: 'none',
                  fontSize: '13px',
                  fontWeight: 500,
                  borderRadius: '6px',
                  borderColor: '#e6e6e6',
                  color: '#444',
                  '&:hover': {
                    borderColor: '#0E4D94',
                    color: '#0E4D94',
                    backgroundColor: 'rgba(14, 77, 148, 0.04)',
                  },
                }}
              >
                {filterAccordionOpen ? 'Filtreleri Gizle' : 'Filtrele'}
              </Button>
            </Stack>

            {/* Filtreler Accordion - sadece açıkken göster */}
            {filterAccordionOpen && (
              <Accordion
                expanded
                onChange={(e, expanded) => { if (!expanded) setFilterAccordionOpen(false); }}
                sx={{
                  boxShadow: 'none',
                  '&:before': { display: 'none' },
                  border: 'none',
                  borderBottom: 'none',
                  margin: 0,
                  '& .MuiAccordionSummary-root': { borderBottom: 'none' },
                  '& .MuiAccordionDetails-root': { borderBottom: 'none' },
                }}
              >
                <AccordionDetails sx={{}}>
                  {hasActiveFilters && (
                    <Stack direction="row" justifyContent="flex-end">
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<ClearIcon sx={{ fontSize: 16 }} />}
                        onClick={handleResetFilters}
                        sx={{
                          textTransform: 'none',
                          fontSize: '12px',
                          fontWeight: 500,
                          borderRadius: '6px',
                          borderColor: '#e6e6e6',
                          color: '#666',
                          '&:hover': { borderColor: '#0E4D94', color: '#0E4D94', backgroundColor: 'rgba(14, 77, 148, 0.04)' },
                        }}
                      >
                        Filtreleri Temizle
                      </Button>
                    </Stack>
                  )}
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
                      gap: 2,
                      alignItems: 'end',
                    }}
                  >
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Ara</Typography>
                      <TextField
                        fullWidth
                        size="small"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="İsim ile ara..."
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start" sx={{ mr: 0 }}>
                              <SearchIcon sx={{ fontSize: 18, color: '#999' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Başlangıç Tarihi</Typography>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={startDate}
                        onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Bitiş Tarihi</Typography>
                      <TextField
                        type="date"
                        fullWidth
                        size="small"
                        value={endDate}
                        onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Durum</Typography>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={statusFilter}
                        onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
                        sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                      >
                        <MenuItem value="">Tümü</MenuItem>
                        {Object.entries(DonationCardPurchaseStatusNames).map(([key, label]) => (
                          <MenuItem key={key} value={key}>{label}</MenuItem>
                        ))}
                      </TextField>
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Satın Alan</Typography>
                      <Autocomplete
                        fullWidth
                        options={purchasedByOptions}
                        loading={purchasedByLoading}
                        getOptionLabel={(o) => o?.fullName || ''}
                        value={purchasedByPerson}
                        onChange={(e, v) => { setPurchasedByPerson(v); setPage(1); }}
                        onInputChange={(e, v) => handlePurchasedBySearch(v)}
                        filterOptions={(x) => x}
                        renderOption={(props, option) => (
                          <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                            <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                            <Typography sx={{ fontSize: '14px', color: '#444444' }}>{option.fullName}</Typography>
                          </Stack>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {purchasedByLoading ? <CircularProgress size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Bağış Yapılan</Typography>
                      <Autocomplete
                        fullWidth
                        options={donatedToOptions}
                        loading={donatedToLoading}
                        getOptionLabel={(o) => o?.fullName || ''}
                        value={donatedToPerson}
                        onChange={(e, v) => { setDonatedToPerson(v); setPage(1); }}
                        onInputChange={(e, v) => handleDonatedToSearch(v)}
                        filterOptions={(x) => x}
                        renderOption={(props, option) => (
                          <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                            <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                            <Typography sx={{ fontSize: '14px', color: '#444444' }}>{option.fullName}</Typography>
                          </Stack>
                        )}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <>
                                  {donatedToLoading ? <CircularProgress size={16} /> : null}
                                  {params.InputProps.endAdornment}
                                </>
                              ),
                            }}
                            sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                          />
                        )}
                      />
                    </Box>
                    <Box>
                      <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500, fontSize: '12px', color: '#666' }}>Sıralama</Typography>
                      <TextField
                        fullWidth
                        select
                        size="small"
                        value={sortBy}
                        onChange={(e) => { setSortBy(Number(e.target.value)); setPage(1); }}
                        sx={{ ...textFieldSx, '& .MuiInputBase-root': { minHeight: 38 } }}
                      >
                        <MenuItem value={0}>Tarihe Göre (Yeni → Eski)</MenuItem>
                        <MenuItem value={1}>Tarihe Göre (Eski → Yeni)</MenuItem>
                        <MenuItem value={2}>Tutara Göre (Yüksek → Düşük)</MenuItem>
                        <MenuItem value={3}>Tutara Göre (Düşük → Yüksek)</MenuItem>
                        <MenuItem value={4}>Adete Göre (Yüksek → Düşük)</MenuItem>
                        <MenuItem value={5}>Adete Göre (Düşük → Yüksek)</MenuItem>
                      </TextField>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            )}

            {/* Tablo */}
            <TableContainer component={Paper} sx={{ py: 1, px: 2.5 }} elevation={0}>
              <Table size="small">
                <TableHead sx={{ bgcolor: 'transparent' }}>
                  <TableRow>
                    <TableCell sx={tableCellHeadSx}>Satın Alan</TableCell>
                    <TableCell sx={tableCellHeadSx}>Bağış Yapılan</TableCell>
                    <TableCell sx={tableCellHeadSx}>Adet</TableCell>
                    <TableCell sx={tableCellHeadSx}>Tutar</TableCell>
                    <TableCell sx={tableCellHeadSx}>Durum</TableCell>
                    <TableCell sx={tableCellHeadSx}>Tarih</TableCell>
                    <TableCell align="right" sx={tableCellHeadSx}>İşlemler</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : purchases.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography sx={{ fontSize: '14px', color: '#a3a3a3' }}>Satın alım bulunamadı</Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    purchases.map((purchase, index) => (
                      <TableRow
                        key={purchase.id}
                        hover
                        sx={{
                          '& .MuiTableCell-root': {
                            borderBottom: index === purchases.length - 1 ? 'none' : '1px dashed #e8e8e8',
                            px: 1.5,
                            py: 1.5,
                            color: '#444444',
                            fontSize: '14px',
                          },
                          '&:hover': { backgroundColor: '#fafafa' },
                        }}
                      >
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              src={resolveGroupsAssetUrl(purchase.purchasedByProfilePictureUrl)}
                              sx={{ width: 32, height: 32 }}
                            />
                            <Typography sx={{ fontSize: '14px', color: '#444444' }}>{purchase.purchasedByFullName}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Avatar
                              src={resolveGroupsAssetUrl(purchase.donatedToProfilePictureUrl)}
                              sx={{ width: 32, height: 32 }}
                            />
                            <Typography sx={{ fontSize: '14px', color: '#444444' }}>{purchase.donatedToFullName}</Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{purchase.quantity}</TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>
                          {purchase.amount?.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          {getCurrencyDisplayLabel(purchase.currency)}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={purchase.statusName || DonationCardPurchaseStatusNames[purchase.status]}
                            size="small"
                            sx={{
                              height: 24,
                              fontSize: '12px',
                              fontWeight: 600,
                              borderRadius: '12px',
                              border: 'none',
                              backgroundColor:
                                purchase.status === DonationCardPurchaseStatus.Paid
                                  ? '#DFFFEA'
                                  : purchase.status === DonationCardPurchaseStatus.AwaitingApproval
                                    ? '#FFF4E6'
                                    : purchase.status === DonationCardPurchaseStatus.Cancelled
                                      ? '#F5F5F5'
                                      : '#E7EDF4',
                              color:
                                purchase.status === DonationCardPurchaseStatus.Paid
                                  ? '#17C653'
                                  : purchase.status === DonationCardPurchaseStatus.AwaitingApproval
                                    ? '#FF9800'
                                    : purchase.status === DonationCardPurchaseStatus.Cancelled
                                      ? '#666666'
                                      : '#0E4D94',
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          {purchase.createdAt
                            ? new Date(purchase.createdAt).toLocaleDateString('tr-TR')
                            : '-'}
                        </TableCell>
                        <TableCell align="right">
                          <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                            <Tooltip title="Detay">
                              <IconButton
                                size="small"
                                onClick={() => handleViewDetail(purchase)}
                                sx={{
                                  backgroundColor: '#E7EDF4',
                                  borderRadius: '6px',
                                  width: 32,
                                  height: 32,
                                  '&:hover': { backgroundColor: '#CFDBEA' },
                                  '& svg': { fontSize: '16px', color: '#0E4D94' },
                                }}
                              >
                                <VisibilityIcon />
                              </IconButton>
                            </Tooltip>
                            {purchase.status === DonationCardPurchaseStatus.AwaitingApproval && (
                              <Tooltip title="Onayla">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedPurchase(purchase);
                                    setApproveDialogOpen(true);
                                  }}
                                  sx={{
                                    backgroundColor: '#DFFFEA',
                                    borderRadius: '6px',
                                    width: 32,
                                    height: 32,
                                    '&:hover': { backgroundColor: '#C8F5D8' },
                                    '& svg': { fontSize: '16px', color: '#17C653' },
                                  }}
                                >
                                  <CheckCircleIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            {purchase.status !== DonationCardPurchaseStatus.Paid &&
                              purchase.status !== DonationCardPurchaseStatus.Cancelled && (
                                <Tooltip title="İptal Et">
                                  <IconButton
                                    size="small"
                                    onClick={() => {
                                      setSelectedPurchase(purchase);
                                      setCancelDialogOpen(true);
                                    }}
                                    sx={{
                                      backgroundColor: '#FFEEF3',
                                      borderRadius: '6px',
                                      width: 32,
                                      height: 32,
                                      '&:hover': { backgroundColor: '#FFD6E0' },
                                      '& svg': { fontSize: '14px', color: '#F8285A' },
                                    }}
                                  >
                                    <CancelIcon />
                                  </IconButton>
                                </Tooltip>
                              )}
                          </Stack>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={(e, v) => setPage(v)}
                  size="small"
                  sx={{
                    '& .MuiPaginationItem-root.Mui-selected': { backgroundColor: '#0E4D94' },
                  }}
                />
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Tab 2: Bağış Kartı Ayarları */}
      {tab === 1 && (
        <Card sx={{ border: '1px solid #f1f1f1', boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)', borderRadius: '8px' }}>
          <CardContent sx={{ p: 2.5 }}>
            {cardLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={3}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography sx={{ fontSize: '18px', fontWeight: 500, color: '#444444' }}>
                    Bağış Kartı Bilgileri
                  </Typography>
                  {card ? (
                    <Stack direction="row" spacing={1}>
                      <Button
                        variant="outlined"
                        size="small"
                        component="label"
                        disabled={imageUploading}
                        sx={{
                          textTransform: 'none',
                          fontSize: 13,
                          borderRadius: '5px',
                          borderColor: '#e6e6e6',
                          color: '#444444',
                          '&:hover': {
                            borderColor: '#0E4D94',
                            color: '#0E4D94',
                            backgroundColor: 'rgba(14, 77, 148, 0.04)',
                          },
                        }}
                      >
                        {imageUploading ? 'Görsel Yükleniyor...' : 'Görseli Değiştir'}
                        <input type="file" accept="image/*" hidden onChange={handleCardImageChange} />
                      </Button>
                      <Button
                        variant="contained"
                        startIcon={<EditIcon sx={{ width: 16 }} />}
                        onClick={handleOpenEditDialog}
                        sx={{
                          textTransform: 'none',
                          fontSize: 13,
                          borderRadius: '5px',
                          backgroundColor: '#E7EDF4',
                          boxShadow: 'none',
                          color: '#0E4D94',
                          '&:hover': {
                            backgroundColor: '#CFDBEA',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        Düzenle
                      </Button>
                    </Stack>
                  ) : (
                    <Button
                      variant="contained"
                      startIcon={<AddIcon sx={{ width: 16 }} />}
                      onClick={handleOpenCreateCardDialog}
                      sx={{
                        textTransform: 'none',
                        fontSize: 13,
                        borderRadius: '5px',
                        backgroundColor: '#E7EDF4',
                        boxShadow: 'none',
                        color: '#0E4D94',
                        '&:hover': {
                          backgroundColor: '#CFDBEA',
                          boxShadow: 'none',
                        },
                      }}
                    >
                      Bağış Kartı Oluştur
                    </Button>
                  )}
                </Stack>

                {card ? (
                  <Stack spacing={3}>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                          Açıklama
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 500 }}>
                          {card.description || '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                          Tutar
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444', fontWeight: 600 }}>
                          {card.amount?.toLocaleString('tr-TR', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}{' '}
                          {getCurrencyDisplayLabel(card.currency)}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                          Durum
                        </Typography>
                        <Chip
                          label={card.isActive ? 'Aktif' : 'Pasif'}
                          size="small"
                          sx={{
                            height: 24,
                            fontSize: '12px',
                            fontWeight: 600,
                            borderRadius: '12px',
                            border: 'none',
                            backgroundColor: card.isActive ? '#E7EDF4' : '#f5f5f5',
                            color: card.isActive ? '#0E4D94' : '#666666',
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                          Logo Gelir Hesabı (Cari Kod)
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '14px',
                            color: '#444444',
                            fontWeight: 500,
                            fontFamily: 'ui-monospace, Menlo, Consolas, monospace',
                          }}
                        >
                          {card.effectiveLogoIncomeArpCode
                            || resolveEffectiveLogoIncomeArpCode(
                              card.logoIncomeArpCode,
                              card.defaultLogoIncomeArpCode || defaultLogoIncomeArpCode,
                              FALLBACK_DONATION_CARD_LOGO_INCOME_ARP_CODE,
                            )}
                        </Typography>
                        {!card.logoIncomeArpCode && (
                          <Typography sx={{ fontSize: '12px', color: '#a3a3a3', mt: 0.5 }}>
                            Sistem varsayılanı kullanılıyor
                          </Typography>
                        )}
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                          Oluşturulma Tarihi
                        </Typography>
                        <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                          {card.createdAt
                            ? new Date(card.createdAt).toLocaleString('tr-TR')
                            : '-'}
                        </Typography>
                      </Grid>
                      {card.updatedAt && (
                        <Grid item xs={12} md={6}>
                          <Typography sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                            Güncellenme Tarihi
                          </Typography>
                          <Typography sx={{ fontSize: '14px', color: '#444444' }}>
                            {new Date(card.updatedAt).toLocaleString('tr-TR')}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>

                    {card.imageUrl && (
                      <Box>
                        <Typography
                          sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}
                        >
                          Kart Görseli
                        </Typography>
                        <Box
                          component="img"
                          src={resolvePaymentsFileUrl(card.imageUrl)}
                          alt="Bağış Kartı"
                          sx={{
                            width: '100%',
                            maxHeight: 360,
                            borderRadius: '10px',
                            border: '1px solid #e6e6e6',
                            objectFit: 'contain',
                            backgroundColor: '#fafafa',
                          }}
                        />
                      </Box>
                    )}
                  </Stack>
                ) : (
                  <Alert severity="warning" sx={{ fontSize: '13px' }}>
                    Bağış kartı bulunamadı
                  </Alert>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>
      )}

      {/* Kart Düzenleme Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Bağış Kartını Düzenle
          <IconButton
            onClick={() => setEditDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Açıklama
              </Typography>
              <TextField
                multiline
                rows={4}
                value={editForm.description}
                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                inputProps={{ maxLength: 1000 }}
                fullWidth
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Tutar
              </Typography>
              <TextField
                type="number"
                value={editForm.amount}
                onChange={(e) => setEditForm({ ...editForm, amount: parseFloat(e.target.value) || 0 })}
                inputProps={{ min: 0.01, max: 999999.99, step: 0.01 }}
                fullWidth
                required
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Para Birimi
              </Typography>
              <TextField
                select
                value={editForm.currency}
                onChange={(e) => setEditForm({ ...editForm, currency: e.target.value })}
                fullWidth
                required
                sx={textFieldSx}
              >
                <MenuItem value="TRY">TL</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Logo Gelir Hesabı (Cari Kod)
              </Typography>
              <TextField
                value={editForm.logoIncomeArpCode}
                onChange={(e) => setEditForm({ ...editForm, logoIncomeArpCode: e.target.value })}
                placeholder={defaultLogoIncomeArpCode}
                inputProps={{ maxLength: 50 }}
                helperText={logoIncomeHelperText}
                fullWidth
                sx={textFieldSx}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={editForm.isActive}
                  onChange={(e) => setEditForm({ ...editForm, isActive: e.target.checked })}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                  }}
                />
              }
              label="Aktif"
            />
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => setEditDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdateCard}
            disabled={saving || !editForm.amount || editForm.amount <= 0}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              backgroundColor: '#0E4D94',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
            }}
          >
            {saving ? <CircularProgress size={20} /> : 'Kaydet'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bağış Kartı Oluşturma Dialog */}
      <Dialog
        open={createCardDialogOpen}
        onClose={() => setCreateCardDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Bağış Kartı Oluştur
          <IconButton
            onClick={() => setCreateCardDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Açıklama
              </Typography>
              <TextField
                multiline
                rows={4}
                value={createCardForm.description}
                onChange={(e) => setCreateCardForm({ ...createCardForm, description: e.target.value })}
                inputProps={{ maxLength: 1000 }}
                fullWidth
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Tutar
              </Typography>
              <TextField
                type="number"
                value={createCardForm.amount}
                onChange={(e) =>
                  setCreateCardForm({ ...createCardForm, amount: parseFloat(e.target.value) || 0 })
                }
                inputProps={{ min: 0.01, max: 999999.99, step: 0.01 }}
                fullWidth
                required
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Para Birimi
              </Typography>
              <TextField
                select
                value={createCardForm.currency}
                onChange={(e) => setCreateCardForm({ ...createCardForm, currency: e.target.value })}
                fullWidth
                required
                sx={textFieldSx}
              >
                <MenuItem value="TRY">TL</MenuItem>
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="EUR">EUR</MenuItem>
              </TextField>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Logo Gelir Hesabı (Cari Kod)
              </Typography>
              <TextField
                value={createCardForm.logoIncomeArpCode}
                onChange={(e) =>
                  setCreateCardForm({ ...createCardForm, logoIncomeArpCode: e.target.value })
                }
                placeholder={defaultLogoIncomeArpCode}
                inputProps={{ maxLength: 50 }}
                helperText={logoIncomeHelperText}
                fullWidth
                sx={textFieldSx}
              />
            </Box>
            <FormControlLabel
              control={
                <Switch
                  checked={createCardForm.isActive}
                  onChange={(e) =>
                    setCreateCardForm({ ...createCardForm, isActive: e.target.checked })
                  }
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: '#0E4D94' },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#0E4D94' },
                  }}
                />
              }
              label="Aktif"
            />
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Kart Görseli (opsiyonel)
              </Typography>
              <Button
                variant="outlined"
                component="label"
                size="small"
                sx={{
                  textTransform: 'none',
                  fontSize: 13,
                  borderRadius: '5px',
                  borderColor: '#e6e6e6',
                  color: '#444444',
                  '&:hover': { borderColor: '#0E4D94', color: '#0E4D94', backgroundColor: 'rgba(14, 77, 148, 0.04)' },
                }}
              >
                Dosya Seç
                <input
                  type="file"
                  accept="image/*"
                  hidden
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setCreateCardForm({ ...createCardForm, imageFile: file });
                  }}
                />
              </Button>
              {createCardForm.imageFile && (
                <Typography variant="body2" sx={{ mt: 0.5, fontSize: '13px', color: '#a3a3a3' }}>
                  {createCardForm.imageFile.name}
                </Typography>
              )}
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => setCreateCardDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleCreateCard}
            disabled={creatingCard || !createCardForm.amount || createCardForm.amount <= 0}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              backgroundColor: '#0E4D94',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
            }}
          >
            {creatingCard ? <CircularProgress size={20} /> : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detay Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => {
          setDetailDialogOpen(false);
          setPurchaseDetail(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Satın Alım Detayı
          <IconButton
            onClick={() => {
              setDetailDialogOpen(false);
              setPurchaseDetail(null);
            }}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : purchaseDetail ? (
            <Stack spacing={2}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                    Satın Alan
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Avatar
                      src={resolveGroupsAssetUrl(purchaseDetail.purchasedByProfilePictureUrl)}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Typography variant="body1">{purchaseDetail.purchasedByFullName}</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                    Bağış Yapılan
                  </Typography>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                    <Avatar
                      src={resolveGroupsAssetUrl(purchaseDetail.donatedToProfilePictureUrl)}
                      sx={{ width: 32, height: 32 }}
                    />
                    <Typography variant="body1">{purchaseDetail.donatedToFullName}</Typography>
                  </Stack>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                    Adet
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {purchaseDetail.quantity}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                    Tutar
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5, fontWeight: 600 }}>
                    {purchaseDetail.amount?.toLocaleString('tr-TR', {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}{' '}
                    {getCurrencyDisplayLabel(purchaseDetail.currency)}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                    Durum
                  </Typography>
                  <Chip
                    label={purchaseDetail.statusName || DonationCardPurchaseStatusNames[purchaseDetail.status]}
                    size="small"
                    sx={{
                      mt: 0.5,
                      height: 24,
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor:
                        purchaseDetail.status === DonationCardPurchaseStatus.Paid
                          ? '#DFFFEA'
                          : purchaseDetail.status === DonationCardPurchaseStatus.AwaitingApproval
                            ? '#FFF4E6'
                            : purchaseDetail.status === DonationCardPurchaseStatus.Cancelled
                              ? '#f5f5f5'
                              : '#E7EDF4',
                      color:
                        purchaseDetail.status === DonationCardPurchaseStatus.Paid
                          ? '#17C653'
                          : purchaseDetail.status === DonationCardPurchaseStatus.AwaitingApproval
                            ? '#FF9800'
                            : purchaseDetail.status === DonationCardPurchaseStatus.Cancelled
                              ? '#666666'
                              : '#0E4D94',
                    }}
                  />
                </Grid>
                {purchaseDetail.paymentMethod !== undefined && purchaseDetail.paymentMethod !== null && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                      Ödeme Yöntemi
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {PaymentMethodNames[purchaseDetail.paymentMethod] || '-'}
                    </Typography>
                  </Grid>
                )}
                {purchaseDetail.referenceNumber && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                      Referans Numarası
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {purchaseDetail.referenceNumber}
                    </Typography>
                  </Grid>
                )}
                {purchaseDetail.paymentDate && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                      Ödeme Tarihi
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {new Date(purchaseDetail.paymentDate).toLocaleString('tr-TR')}
                    </Typography>
                  </Grid>
                )}
                {purchaseDetail.description && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                      Açıklama
                    </Typography>
                    <Typography variant="body1" sx={{ mt: 0.5 }}>
                      {purchaseDetail.description}
                    </Typography>
                  </Grid>
                )}
                {purchaseDetail.paymentProofUrl && (
                  <Grid item xs={12}>
                    <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                      Ödeme Kanıtı
                    </Typography>
                    <Button
                      href={resolvePaymentsFileUrl(purchaseDetail.paymentProofUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      size="small"
                      sx={{ mt: 0.5 }}
                    >
                      Görüntüle
                    </Button>
                  </Grid>
                )}
                <Grid item xs={12}>
                  <DonationLogoInfoSection detail={purchaseDetail} />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ fontSize: '13px', color: '#a3a3a3', mb: 0.5 }}>
                    Oluşturulma Tarihi
                  </Typography>
                  <Typography variant="body1" sx={{ mt: 0.5 }}>
                    {purchaseDetail.createdAt
                      ? new Date(purchaseDetail.createdAt).toLocaleString('tr-TR')
                      : '-'}
                  </Typography>
                </Grid>
              </Grid>
            </Stack>
          ) : (
            <Alert severity="error">Detay yüklenemedi</Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => {
              setDetailDialogOpen(false);
              setPurchaseDetail(null);
            }}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            Kapat
          </Button>
          {canCancel && (
            <Button
              onClick={() => {
                setDetailDialogOpen(false);
                setCancelDialogOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                color: '#F8285A',
                '&:hover': { backgroundColor: 'rgba(248, 40, 90, 0.04)' },
              }}
            >
              İptal Et
            </Button>
          )}
          {canApprove && (
            <Button
              variant="contained"
              onClick={() => {
                setDetailDialogOpen(false);
                setApproveDialogOpen(true);
              }}
              sx={{
                textTransform: 'none',
                fontSize: '13px',
                borderRadius: '5px',
                backgroundColor: '#17C653',
                boxShadow: 'none',
                '&:hover': { backgroundColor: '#14a048', boxShadow: 'none' },
              }}
            >
              Onayla
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Onay Dialog */}
      <Dialog open={approveDialogOpen} onClose={() => setApproveDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Ödemeyi Onayla
          <IconButton
            onClick={() => setApproveDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: '13px' }}>
              Havale onayı kaydı ödendi yapar; Logo fişi oluşturmaz.
            </Alert>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Ödeme Tarihi
              </Typography>
              <TextField
                type="datetime-local"
                value={approvePaymentDate}
                onChange={(e) => setApprovePaymentDate(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
                sx={textFieldSx}
              />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                Notlar
              </Typography>
              <TextField
                multiline
                rows={4}
                value={approveNotes}
                onChange={(e) => setApproveNotes(e.target.value)}
                inputProps={{ maxLength: 1000 }}
                fullWidth
                placeholder="Opsiyonel notlar..."
                sx={textFieldSx}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => setApproveDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleApprove}
            disabled={processing}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              backgroundColor: '#17C653',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#14a048', boxShadow: 'none' },
            }}
          >
            {processing ? <CircularProgress size={20} /> : 'Onayla'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* İptal Dialog */}
      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Satın Alımı İptal Et
          <IconButton
            onClick={() => setCancelDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                İptal Nedeni
              </Typography>
              <TextField
                multiline
                rows={4}
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                inputProps={{ maxLength: 1000 }}
                fullWidth
                placeholder="İptal nedeni giriniz..."
                sx={textFieldSx}
              />
            </Box>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => setCancelDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleCancel}
            disabled={processing}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              backgroundColor: '#F8285A',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#d61f4a', boxShadow: 'none' },
            }}
          >
            {processing ? <CircularProgress size={20} /> : 'İptal Et'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Yeni Bağış Ekle Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle
          sx={{
            fontSize: '18px',
            fontWeight: 500,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            pr: 3,
          }}
        >
          Yeni Bağış Ekle
          <IconButton
            onClick={() => setCreateDialogOpen(false)}
            size="small"
            sx={{
              color: 'text.secondary',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ py: 2, px: 3 }}>
          <Stack spacing={2}>
            <Alert severity="info" sx={{ fontSize: '13px' }}>
              Tutar backend tarafından kart fiyatı × adet ile hesaplanır. Manuel kayıtlar Logo fişi oluşturmaz.
            </Alert>
            <Autocomplete
              options={purchasedBySearchOptions}
              loading={purchasedBySearchLoading}
              getOptionLabel={(o) => o?.fullName || ''}
              value={createForm.purchasedByPerson}
              onChange={(e, v) => {
                if (v) {
                  setCreateForm({
                    ...createForm,
                    purchasedByPerson: v,
                    purchasedByType: v.type || 0,
                  });
                } else {
                  setCreateForm({
                    ...createForm,
                    purchasedByPerson: null,
                    purchasedByType: 0,
                  });
                }
              }}
              onInputChange={(e, v) => handlePurchasedBySearchForCreate(v)}
              filterOptions={(x) => x}
              renderOption={(props, option) => (
                <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                  <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                  <Typography variant="body2">{option.fullName}</Typography>
                  <Chip
                    label={option.type === 1 ? 'Yakın' : 'Üye'}
                    size="small"
                    sx={{
                      ml: 'auto',
                      height: 24,
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: option.type === 1 ? '#FFF4E6' : '#E7EDF4',
                      color: option.type === 1 ? '#FF9800' : '#0E4D94',
                    }}
                  />
                </Stack>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Satın Alan"
                  size="small"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {purchasedBySearchLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={textFieldSx}
                />
              )}
            />

            <Autocomplete
              options={donatedToSearchOptions}
              loading={donatedToSearchLoading}
              getOptionLabel={(o) => o?.fullName || ''}
              value={createForm.donatedToPerson}
              onChange={(e, v) => {
                if (v) {
                  setCreateForm({
                    ...createForm,
                    donatedToPerson: v,
                    donatedToType: v.type || 0,
                  });
                } else {
                  setCreateForm({
                    ...createForm,
                    donatedToPerson: null,
                    donatedToType: 0,
                  });
                }
              }}
              onInputChange={(e, v) => handleDonatedToSearchForCreate(v)}
              filterOptions={(x) => x}
              renderOption={(props, option) => (
                <Stack component="li" direction="row" spacing={1} alignItems="center" {...props} key={option.id}>
                  <Avatar src={resolveGroupsAssetUrl(option.profilePictureUrl)} sx={{ width: 28, height: 28 }} />
                  <Typography variant="body2">{option.fullName}</Typography>
                  <Chip
                    label={option.type === 1 ? 'Yakın' : 'Üye'}
                    size="small"
                    sx={{
                      ml: 'auto',
                      height: 24,
                      fontSize: '12px',
                      fontWeight: 600,
                      borderRadius: '12px',
                      border: 'none',
                      backgroundColor: option.type === 1 ? '#FFF4E6' : '#E7EDF4',
                      color: option.type === 1 ? '#FF9800' : '#0E4D94',
                    }}
                  />
                </Stack>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bağış Yapılan"
                  size="small"
                  required
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {donatedToSearchLoading ? <CircularProgress size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                  sx={textFieldSx}
                />
              )}
            />

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Adet
                  </Typography>
                  <TextField
                    type="number"
                    value={createForm.quantity}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, quantity: parseInt(e.target.value) || 1 })
                    }
                    inputProps={{ min: 1, max: 999 }}
                    fullWidth
                    required
                    sx={textFieldSx}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Ödeme Yöntemi
                  </Typography>
                  <TextField
                    select
                    value={createForm.paymentMethod}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, paymentMethod: Number(e.target.value) })
                    }
                    fullWidth
                    required
                    sx={textFieldSx}
                  >
                    <MenuItem value={0}>Nakit</MenuItem>
                    <MenuItem value={1}>Banka Havalesi/EFT</MenuItem>
                    <MenuItem value={2}>Kredi Kartı</MenuItem>
                    <MenuItem value={3}>Online Ödeme</MenuItem>
                  </TextField>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Referans Numarası
                  </Typography>
                  <TextField
                    value={createForm.referenceNumber}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, referenceNumber: e.target.value })
                    }
                    inputProps={{ maxLength: 100 }}
                    fullWidth
                    sx={textFieldSx}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Ödeme Tarihi
                  </Typography>
                  <TextField
                    type="datetime-local"
                    value={createForm.paymentDate}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, paymentDate: e.target.value })
                    }
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    sx={textFieldSx}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Açıklama
                  </Typography>
                  <TextField
                    multiline
                    rows={4}
                    value={createForm.description}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, description: e.target.value })
                    }
                    inputProps={{ maxLength: 1000 }}
                    fullWidth
                    sx={textFieldSx}
                  />
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ mb: 0.6, fontWeight: 500, fontSize: '13px', color: '#444444' }}>
                    Ödeme Kanıtı URL
                  </Typography>
                  <TextField
                    value={createForm.paymentProofUrl}
                    onChange={(e) =>
                      setCreateForm({ ...createForm, paymentProofUrl: e.target.value })
                    }
                    inputProps={{ maxLength: 500 }}
                    fullWidth
                    placeholder="https://..."
                    sx={textFieldSx}
                  />
                </Box>
              </Grid>
            </Grid>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid #f0f0f0' }}>
          <Button
            onClick={() => setCreateDialogOpen(false)}
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              color: '#666666',
              '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' },
            }}
          >
            İptal
          </Button>
          <Button
            variant="contained"
            onClick={handleCreatePurchase}
            disabled={
              creating ||
              !createForm.purchasedByPerson ||
              !createForm.donatedToPerson ||
              !createForm.quantity ||
              createForm.quantity < 1 ||
              createForm.quantity > 999
            }
            sx={{
              textTransform: 'none',
              fontSize: '13px',
              borderRadius: '5px',
              backgroundColor: '#0E4D94',
              boxShadow: 'none',
              '&:hover': { backgroundColor: '#0a3a70', boxShadow: 'none' },
            }}
          >
            {creating ? <CircularProgress size={20} /> : 'Oluştur'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DonationCards;
